import {
  IonButton,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
} from '@ionic/react';
import { close } from 'ionicons/icons';
import React, { useCallback, useContext, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { DATE_FORMAT } from '../../constants';
import { TranslationContext } from '../../contexts';
import { resourceHelper } from '../../helpers';
import { Conversation } from '../../models';
import {
  conversationResource,
  customerResource,
  expertResource,
} from '../../resource';
import { Female, Male, Search, Time } from '../icons';

interface Props {
  onClose: () => void;
}

export const ConversationSearch: React.FC<Props> = ({ onClose }) => {
  const { translate, translateDate } = useContext(TranslationContext);
  const history = useHistory();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Conversation[]>([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchConversations = useCallback(async (searchQuery: string) => {
    if (searchQuery.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const response = await conversationResource.findAll({
        'customer.name': searchQuery.trim(),
        'order[updatedAt]': 'desc',
        perPage: 20,
        page: 1,
      });
      setResults(response.members);
    } catch {
      setResults([]);
    }
    setSearching(false);
  }, []);

  const handleInputChange = (value: string) => {
    setQuery(value);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      searchConversations(value);
    }, 300);
  };

  const handleSelect = (conversation: Conversation) => {
    const idCustomer = resourceHelper.getIdFromIri(
      conversation.customer['@id'],
      customerResource,
    );
    const idExpert = resourceHelper.getIdFromIri(
      conversation.expert['@id'],
      expertResource,
    );
    history.push(`/expert/conversation/${idExpert}/${idCustomer}`, {
      idConversation: conversation['@id'],
      readOnly: conversation.status === 'finished',
    });
    onClose();
  };

  const handleClose = () => {
    setQuery('');
    setResults([]);
    onClose();
  };

  return (
    <div className="conversation-search">
      <div className="conversation-search-input">
        <Search />
        <IonInput
          value={query}
          placeholder={translate('menu.conversations.searchPlaceholder')}
          onIonInput={e => handleInputChange(e.detail.value || '')}
          autofocus
          debounce={0}
        />
        <IonButton fill="clear" size="small" onClick={handleClose}>
          <IonIcon icon={close} />
        </IonButton>
      </div>
      {searching && (
        <IonItem lines="none">
          <IonLabel>{translate('ui.loading')}</IonLabel>
        </IonItem>
      )}
      {!searching && query.trim().length >= 2 && results.length === 0 && (
        <IonItem lines="none">
          <IonLabel>{translate('menu.conversations.noResults')}</IonLabel>
        </IonItem>
      )}
      {results.length > 0 && (
        <IonList className="conversation-search-results">
          {results.map(conversation => (
            <IonItem
              className="conversation-item"
              key={conversation['@id']}
              lines="none"
              onClick={() => handleSelect(conversation)}
              detail
            >
              <div>
                <IonLabel>
                  {conversation.customer.gender ? (
                    <span style={{ marginRight: '2px' }}>
                      {conversation.customer.gender.toLowerCase() === 'man' ? (
                        <Male />
                      ) : (
                        <Female />
                      )}
                    </span>
                  ) : null}
                  {conversation.customer.name} (#{conversation.id},{' '}
                  {conversation.messageCount} messages)
                </IonLabel>
                {conversation.updatedAt ? (
                  <IonNote>
                    <Time />{' '}
                    {translateDate(conversation.updatedAt, DATE_FORMAT.FULL)}
                  </IonNote>
                ) : null}
              </div>
            </IonItem>
          ))}
        </IonList>
      )}
    </div>
  );
};
